/**
 * WebPlacementDeployService
 *
 * Orchestrates web placement deployment:
 *
 * For single (funnel) placements:
 * 1. Load web placement from Firestore
 * 2. Load full V2 FlowDocument via ConfigLoader
 * 3. Call BuilderApiClient.publishWeb()
 * 4. Update status to 'published' via WebPlacementService
 *
 * For A/B test placements:
 * 1. Load web placement from Firestore
 * 2. Validate all variant placements are published
 * 3. Generate abSalt if needed
 * 4. Upload routing config to GCS: web-ab/{alias}.json
 * 5. Update status to 'published' via WebPlacementService
 *
 * Reuses BuilderApiClient from placements module to avoid duplication.
 *
 * @module webplacements/services
 */

import {
  WebABPublicationError,
  WebPlacementService,
} from '@modules/firebase-core/services/WebPlacementService';
import { WebPlacementConfigLoader } from './WebPlacementConfigLoader';
import { BuilderApiClientImpl } from '@modules/placements/services/BuilderApiClientImpl';
import { ProjectAnalyticsService } from '@modules/analytics-settings/services/ProjectAnalyticsService';
import type { BuilderApiClient } from '@modules/placements/services/BuilderApiClient';
import { PublishWebError } from '@modules/placements/services/BuilderApiClient';
import type {
  PrivacyPreflightResult,
  PrivacyPreflightVariable,
  PublishWebHealthcheckIssue,
  UnreviewedAnalyticsVariable,
  WebPlacement,
  WebPlacementDeployParams,
  WebPlacementDeployResult,
} from '@modules/webplacements/types/webplacements.types';
import { getWebFunnelUrl, isABTestPlacement } from '@modules/webplacements/types/webplacements.types';
import {
  buildWebABIterationSnapshot,
  buildWebABRoutingConfig,
} from './WebABIterationBuilder';
import { getWebPlacementBuilderApiUrl } from './webPlacementEnvironment';
import type { PublishGateFinding } from '@modules/shared-types/src/launchActivation.types';
import { buildMissingAttributionFinding, mergeGateFindings } from './publishGate';

class WebABArtifactRollbackError extends Error {
  readonly code = 'ab_artifact_rollback_failed';

  constructor(message: string) {
    super(message);
    this.name = 'WebABArtifactRollbackError';
  }
}

export interface WebABTestDeleteResult {
  success: boolean;
  /** 'deleted' — artifact CAS-removed; 'skipped' — nothing published; 'failed' — best-effort cleanup lost, placement still deleted. */
  artifactCleanup: 'deleted' | 'skipped' | 'failed';
}

export interface PublishGateResult {
  privacy: PrivacyPreflightResult;
  findings: PublishGateFinding[];
  /** The healthcheck could not be evaluated (network / 503) — publish proceeds, the server gate stays authoritative. */
  unavailable: boolean;
}

/**
 * WebPlacementDeployService
 *
 * Orchestrates the full deployment flow for web placements.
 *
 * Usage:
 * ```typescript
 * const deployService = new WebPlacementDeployService();
 * const result = await deployService.deploy({
 *   projectId: 'my-project',
 *   webPlacementId: 'abc123',
 *   userId: 'user@example.com'
 * });
 *
 * if (result.success) {
 *   console.log('Published to:', result.webUrl);
 * }
 * ```
 */
export class WebPlacementDeployService {
  private readonly webPlacementService: WebPlacementService;
  private readonly configLoader: WebPlacementConfigLoader;
  private readonly builderClient: BuilderApiClient;
  private readonly analyticsService: ProjectAnalyticsService;

  constructor(
    webPlacementService?: WebPlacementService,
    configLoader?: WebPlacementConfigLoader,
    builderClient?: BuilderApiClient,
    analyticsService?: ProjectAnalyticsService
  ) {
    this.webPlacementService = webPlacementService || new WebPlacementService();
    this.configLoader = configLoader || new WebPlacementConfigLoader();
    this.builderClient = builderClient || this.createDefaultBuilderClient();
    this.analyticsService = analyticsService || new ProjectAnalyticsService();
  }

  /**
   * Create default BuilderApiClient from environment
   */
  private createDefaultBuilderClient(): BuilderApiClient {
    const builderUrl = getWebPlacementBuilderApiUrl();
    if (!builderUrl) {
      throw new Error(
        'VITE_BUILDER_API_URL is not set. ' +
        'Please configure the Builder Service URL in your environment.'
      );
    }
    return new BuilderApiClientImpl(builderUrl);
  }

  /**
   * Delete an A/B test placement together with its GCS routing artifact.
   *
   * The artifact is CAS-deleted first (expected hash = the activated pointer),
   * so a recreated test can reuse the alias without hitting the orphan-artifact
   * 409 on its first publish. Cleanup is best-effort: a conflict or network
   * failure is logged and the Firestore deletion still proceeds — blocking the
   * user's delete on a stale artifact would be worse than the orphan it leaves.
   */
  async deleteABTest(projectId: string, webPlacementId: string): Promise<WebABTestDeleteResult> {
    const webPlacement = await this.webPlacementService.get(projectId, webPlacementId);
    if (!webPlacement) {
      return { success: true, artifactCleanup: 'skipped' };
    }

    let artifactCleanup: WebABTestDeleteResult['artifactCleanup'] = 'skipped';
    const routingHash = isABTestPlacement(webPlacement)
      ? webPlacement.abCurrentIteration?.routingHash
      : undefined;
    if (routingHash) {
      try {
        const cleanup = await this.builderClient.deleteABRoutingConfig({
          alias: webPlacement.alias,
          projectId,
          expectedRoutingHash: routingHash,
        });
        artifactCleanup = cleanup.success ? 'deleted' : 'failed';
        if (!cleanup.success) {
          console.warn(
            `[WebPlacementDeployService] A/B routing artifact cleanup failed for "${webPlacement.alias}": ${cleanup.error}`
          );
        }
      } catch (error) {
        artifactCleanup = 'failed';
        console.warn(
          `[WebPlacementDeployService] A/B routing artifact cleanup failed for "${webPlacement.alias}": ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    await this.webPlacementService.delete(projectId, webPlacementId);
    return { success: true, artifactCleanup };
  }

  /**
   * Deploy a web placement
   *
   * Routes to appropriate deploy method based on placement type:
   * - single: Full SSG deployment via Builder Service
   * - ab_test: Routing config upload to GCS
   *
   * @param params - Deploy parameters
   * @returns Deploy result with success status and web URL
   */
  async deploy(params: WebPlacementDeployParams): Promise<WebPlacementDeployResult> {
    const { projectId, webPlacementId, userId } = params;

    console.log(`[WebPlacementDeployService] Starting deployment: ${webPlacementId}`);

    try {
      // 1. Get web placement from Firestore
      const webPlacement = await this.webPlacementService.get(projectId, webPlacementId);

      if (!webPlacement) {
        return {
          success: false,
          webUrl: '',
          gcsFiles: [],
          error: `Web placement "${webPlacementId}" not found`,
        };
      }

      // Route based on type
      if (isABTestPlacement(webPlacement)) {
        return await this.deployABTest(params, webPlacement);
      }

      return await this.deploySingle(params, webPlacement);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[WebPlacementDeployService] Deployment failed: ${errorMessage}`);

      return {
        success: false,
        webUrl: '',
        gcsFiles: [],
        error: errorMessage,
        ...(
          error instanceof WebABPublicationError || error instanceof WebABArtifactRollbackError
            ? { errorCode: error.code }
            : error instanceof PublishWebError
              ? {
                  ...(error.code ? { errorCode: error.code } : {}),
                  ...(error.issues?.length
                    ? { issues: error.issues as PublishWebHealthcheckIssue[] }
                    : {}),
                }
              : {}
        ),
      };
    }
  }

  /**
   * SERVER-driven publish privacy preflight (side-effect free).
   *
   * Evaluates `POST /api/publish/web/healthcheck` with the SAME config the
   * deploy path would send, so the review list can never diverge from the
   * publish gate. Single placements check their own funnel; A/B tests check
   * every variant's referenced placement funnel (labeled by variant name).
   *
   * Never throws: `unavailable` means the gate could not be evaluated —
   * callers should proceed to publish (the server gate stays authoritative).
   *
   * Signature unchanged (existing callers untouched) — delegates to
   * `runPublishGate` so the privacy result can never diverge from the gate.
   */
  async preflightPrivacyReview(
    projectId: string,
    webPlacement: WebPlacement
  ): Promise<PrivacyPreflightResult> {
    return (await this.runPublishGate(projectId, webPlacement)).privacy;
  }

  /**
   * Publish gate (DC-21): the SAME `POST /api/publish/web/healthcheck` call as the privacy
   * preflight, now also returning the tier-2 `findings` (Plan A server) merged with the
   * client-computed attribution finding (DC-10). Never throws.
   */
  async runPublishGate(projectId: string, webPlacement: WebPlacement): Promise<PublishGateResult> {
    try {
      const targets = await this.resolvePreflightTargets(projectId, webPlacement);
      if (targets.length === 0) {
        return { privacy: { status: 'passed', unreviewed: [] }, findings: [], unavailable: false };
      }

      const settings = await this.analyticsService.getSettings(projectId).catch(() => null);
      const integrations = settings?.integrations ?? [];
      const unreviewed: PrivacyPreflightVariable[] = [];
      const serverFindings: PublishGateFinding[] = [];
      const clientFindings: PublishGateFinding[] = [];
      let anyUnavailable = false;

      for (const target of targets) {
        const loadResult = await this.configLoader.loadConfig(projectId, target.placement);
        const check = await this.builderClient.publishWebHealthcheck({
          config: loadResult.flowDocument,
          projectId,
        });

        if (check.code === 'analytics_variable_privacy_review_required') {
          for (const rawIssue of check.healthcheck?.issues ?? []) {
            const issue = rawIssue as PublishWebHealthcheckIssue;
            const evidence = issue?.evidence?.unreviewedAnalyticsVariables ?? [];
            for (const variable of evidence as UnreviewedAnalyticsVariable[]) {
              if (!variable?.variableId) continue;
              unreviewed.push({
                funnelId: loadResult.funnelId,
                versionId: loadResult.versionId,
                ...(target.variantLabel ? { variantLabel: target.variantLabel } : {}),
                variableId: variable.variableId,
                ...(variable.variableName ? { variableName: variable.variableName } : {}),
              });
            }
          }
        } else if (!check.success) {
          // e.g. analytics_privacy_settings_unavailable (503) — cannot evaluate
          anyUnavailable = true;
        }
        if (Array.isArray(check.findings)) serverFindings.push(...check.findings);

        const attribution = buildMissingAttributionFinding(target.placement.config, integrations, target.variantLabel);
        if (attribution) clientFindings.push(attribution);
      }

      const privacy: PrivacyPreflightResult = unreviewed.length > 0
        ? { status: 'review_required', unreviewed }
        : anyUnavailable
          ? { status: 'unavailable', unreviewed: [] }
          : { status: 'passed', unreviewed: [] };

      // MIN-3: an unavailable target does NOT erase what the other targets reported.
      // `unavailable` says "the picture may be incomplete"; the findings that WERE
      // collected still stand (an A/B test whose second variant 503s must not lose
      // the first variant's blocking finding).
      return {
        privacy,
        findings: mergeGateFindings(serverFindings, clientFindings),
        unavailable: anyUnavailable,
      };
    } catch (error) {
      console.warn(
        '[WebPlacementDeployService] Publish gate unavailable — publish will rely on the server gate:',
        error
      );
      return { privacy: { status: 'unavailable', unreviewed: [] }, findings: [], unavailable: true };
    }
  }

  /**
   * Funnel targets the privacy preflight inspects: the placement's own funnel
   * for single placements, every variant's referenced placement funnel for
   * A/B tests (deduped by funnel/version, labeled by variant name).
   */
  private async resolvePreflightTargets(
    projectId: string,
    webPlacement: WebPlacement
  ): Promise<Array<{ placement: WebPlacement; variantLabel?: string }>> {
    if (!isABTestPlacement(webPlacement)) {
      return webPlacement.config?.funnelId && webPlacement.config?.versionId
        ? [{ placement: webPlacement }]
        : [];
    }

    const variants = webPlacement.variants ?? [];
    const placements = await Promise.all(
      variants.map((variant) => this.webPlacementService.get(projectId, variant.webPlacementId))
    );

    const targets: Array<{ placement: WebPlacement; variantLabel?: string }> = [];
    const seen = new Set<string>();
    placements.forEach((placement, index) => {
      const funnelId = placement?.config?.funnelId;
      const versionId = placement?.config?.versionId;
      if (!placement || !funnelId || !versionId) return;
      const scopeKey = `${funnelId}:${versionId}`;
      if (seen.has(scopeKey)) return;
      seen.add(scopeKey);
      targets.push({ placement, variantLabel: variants[index]?.name });
    });
    return targets;
  }

  /**
   * Deploy a single (funnel) web placement
   *
   * Flow:
   * 1. Load full V2 FlowDocument via ConfigLoader
   * 2. Call BuilderApiClient.publishWeb() for upload
   * 3. Update status to 'published' via WebPlacementService.markPublished()
   */
  private async deploySingle(
    params: WebPlacementDeployParams,
    webPlacement: WebPlacement
  ): Promise<WebPlacementDeployResult> {
    const { projectId, webPlacementId, userId } = params;

    // 1. Load full V2 FlowDocument via ConfigLoader
    console.log(`[WebPlacementDeployService] Loading config for alias: ${webPlacement.alias}`);

    const loadResult = await this.configLoader.loadConfig(projectId, webPlacement);

    const screenCount = Object.keys(loadResult.flowDocument.screens || {}).length;
    const detectedSchemaVersion = loadResult.flowDocument.schemaVersion;
    console.log(
      `[WebPlacementDeployService] ✅ Loaded ${screenCount} screens | Schema: ${detectedSchemaVersion}`
    );

    // 2. Load project-level analytics settings
    console.log(`[WebPlacementDeployService] Loading project analytics settings...`);

    const projectAnalytics = await this.analyticsService.getSettings(projectId);
    const enabledIntegrations = projectAnalytics.integrations.filter(i => i.enabled);

    console.log(`[WebPlacementDeployService] Found ${enabledIntegrations.length} enabled analytics integrations`);

    // 3. Call BuilderApiClient.publishWeb() for SSG upload
    console.log(`[WebPlacementDeployService] Publishing to Builder Service...`);

    const publishResult = await this.builderClient.publishWeb({
      alias: webPlacement.alias,
      funnelId: loadResult.funnelId,
      versionId: loadResult.versionId,
      config: loadResult.flowDocument,
      projectId,
      // Pass through WebPlacement config for baking (parameters, SEO, analytics)
      // Include id and entityName from WebPlacement for analytics metadata
      webPlacementConfig: {
        ...loadResult.webPlacementConfig,
        id: webPlacement.id,            // Firestore document ID for webOnboardingId
        entityName: webPlacement.name,  // Placement name for webOnboardingName (NOT alias!)
      },
      // Project-level analytics settings (Amplitude, GA4, GTM, etc.)
      projectAnalytics,
    });

    if (!publishResult.success) {
      return {
        success: false,
        webUrl: publishResult.webUrl || '',
        gcsFiles: [],
        error: 'Builder Service reported failure',
      };
    }

    console.log(`[WebPlacementDeployService] Published to: ${publishResult.webUrl}`);

    // 4. Update status to 'published' via WebPlacementService.markPublished()
    await this.webPlacementService.markPublished(
      projectId,
      webPlacementId,
      userId,
      publishResult.webUrl,
      publishResult.webShellVersion
    );

    console.log(`[WebPlacementDeployService] Deployment complete: ${webPlacementId}`);

    return {
      success: true,
      webUrl: publishResult.webUrl,
      gcsFiles: [publishResult.gcsPath],
    };
  }

  /**
   * Deploy an A/B test web placement
   *
   * Flow:
   * 1. Validate all variant placements are published
   * 2. Generate abSalt if needed
   * 3. Build or reuse the current A/B iteration snapshot
   * 4. Upload routing config to GCS: web-ab/{alias}.json
   * 5. Update status to 'published' via WebPlacementService.markABTestPublished()
   *
   * Key difference from single deploy:
   * - No SSG generation (variants are already published)
   * - Uploads lightweight routing config (~200 bytes)
   */
  private async deployABTest(
    params: WebPlacementDeployParams,
    webPlacement: WebPlacement
  ): Promise<WebPlacementDeployResult> {
    const { projectId, webPlacementId, userId } = params;

    console.log(`[WebPlacementDeployService] Deploying A/B test: ${webPlacement.alias}`);

    // 1. Validate all variant placements are published
    const validation = await this.webPlacementService.validateABTestReferences(projectId, webPlacement);

    if (!validation.valid) {
      return {
        success: false,
        webUrl: '',
        gcsFiles: [],
        error: `A/B test validation failed: ${validation.errors.join(', ')}`,
      };
    }

    console.log(`[WebPlacementDeployService] A/B test validation passed (${webPlacement.variants?.length} variants)`);

    // 2. Generate abSalt if needed
    let abSalt = webPlacement.abSalt;
    if (!abSalt) {
      abSalt = `salt_${crypto.randomUUID().replace(/-/g, '').substring(0, 16)}`;
      // Update the placement with the new salt
      await this.webPlacementService.update(projectId, webPlacementId, { abSalt });
      console.log(`[WebPlacementDeployService] Generated new abSalt for A/B test`);
    }

    // 3. Build or reuse current iteration snapshot. Rich metadata stays in
    // Firestore; the routing config only carries ids and routing-critical data.
    const now = new Date().toISOString();
    const iteration = buildWebABIterationSnapshot({
      webPlacement: { ...webPlacement, abSalt },
      abSalt,
      publishedAt: now,
      publishedBy: userId,
      publishInput: params.abIteration,
    });
    const epochAction = webPlacement.abCurrentIteration?.id === iteration.id
      ? 'reused' as const
      : 'created' as const;
    const activatedIteration = epochAction === 'created'
      ? { ...iteration, publishedAt: now }
      : iteration;
    const routingConfig = buildWebABRoutingConfig(webPlacement, activatedIteration);

    // Persist/verify the immutable epoch before exposing it through the live
    // routing alias. The parent pointer intentionally moves only after the
    // upload; this ordering guarantees that Edge can never emit an iteration
    // id that the decision layer cannot resolve.
    await this.webPlacementService.prepareABTestIteration(
      projectId,
      webPlacementId,
      activatedIteration,
      webPlacement.abCurrentIteration
        ? {
            id: webPlacement.abCurrentIteration.id,
            routingHash: webPlacement.abCurrentIteration.routingHash,
          }
        : null
    );

    // 4. Upload routing config to GCS (project-scoped path)
    const gcsPath = `web-ab/${projectId}/${webPlacement.alias}.json`;
    console.log(`[WebPlacementDeployService] Uploading routing config to GCS: ${gcsPath}`);

    const uploadResult = await this.builderClient.uploadABRoutingConfig({
      alias: webPlacement.alias,
      routingConfig,
      projectId,
      expectedRoutingHash: webPlacement.abCurrentIteration?.routingHash || null,
    });

    if (!uploadResult.success) {
      return {
        success: false,
        webUrl: '',
        gcsFiles: [],
        error: uploadResult.error || 'Failed to upload A/B routing config',
        ...(uploadResult.errorCode ? { errorCode: uploadResult.errorCode } : {}),
      };
    }

    // 5. Build the web URL (same pattern as single placements)
    const webUrl = this.buildWebUrl(webPlacement.alias);

    // 6. Update status to 'published' and activate the prepared iteration.
    try {
      await this.webPlacementService.markABTestPublished(
        projectId,
        webPlacementId,
        userId,
        webUrl,
        activatedIteration,
        webPlacement.abCurrentIteration
          ? {
              id: webPlacement.abCurrentIteration.id,
              routingHash: webPlacement.abCurrentIteration.routingHash,
            }
          : null
      );
    } catch (persistenceError) {
      const rollbackResult = webPlacement.abCurrentIteration
        ? await this.builderClient.uploadABRoutingConfig({
            alias: webPlacement.alias,
            routingConfig: buildWebABRoutingConfig(webPlacement, webPlacement.abCurrentIteration),
            projectId,
            expectedRoutingHash: activatedIteration.routingHash,
          })
        : await this.builderClient.deleteABRoutingConfig({
            alias: webPlacement.alias,
            projectId,
            expectedRoutingHash: activatedIteration.routingHash,
          });

      if (!rollbackResult.success) {
        throw new WebABArtifactRollbackError(
          'A/B publication persistence failed and the routing artifact could not be restored safely.'
        );
      }

      throw persistenceError;
    }

    console.log(`[WebPlacementDeployService] A/B test deployment complete: ${webPlacement.alias}`);

    return {
      success: true,
      webUrl,
      gcsFiles: [gcsPath],
      abIterationId: activatedIteration.id,
      abEpochAction: epochAction,
    };
  }

  /**
   * Build the web URL for a placement alias
   */
  private buildWebUrl(alias: string): string {
    return getWebFunnelUrl(alias);
  }
}
